import { useState, useEffect, useRef } from "react";

import Button from "react-bootstrap/Button";
import { useWizard } from "react-use-wizard";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import ReactPixel from "react-facebook-pixel";
import TiktokPixel from "tiktok-pixel";
import { useCheckoutContext } from "context/CheckoutContext";
import SummaryTable from "components/SummaryTable";
import { Container } from "react-bootstrap";
import {
  recalculateCart,
  validateAddress,
  checkEmailExists,
  checkPhoneExists,
  IsWebaliasAvailable,
} from "api";
import Spinner from "react-bootstrap/Spinner";
import { SHOPIFY_LOGIN_URL } from "config";
import { regexEmail, regexPhone } from "../utils/regex";
import { emailExistsMessage, phoneExistsMessage } from "../utils/messages";
import { formatPhoneNumber } from "../utils/formaters";
import Select, { PHYSICAL_COUNTRIES, VIRTUAL_COUNTRIES } from "components/Select";
import {
  getValidStoredBirthdate,
  getBirthdateFromUrl,
  saveBirthDateToLocalStorage,
} from "../utils/dateUtils";

const ShippingAddress = ({ addresses = [], contact, referral }) => {
  const [validated, setValidated] = useState(false);
  const { nextStep } = useWizard();
  const [isCalculating, setCalculating] = useState(false);
  const [passwordMatch, setPasswordMatch] = useState(true);
  const [addressConfirmed, setAddressConfirmed] = useState(false);
  const [showAddressConfirmation, setShowAddressConfirmation] = useState(false);
  const filteredAddresses = addresses.filter(
    (address) => address.address1 && address.address1.trim() !== ""
  );
  const [emailState, setEmailState] = useState({
    error: "",
    valid: "",
    isLoading: false,
  });
  const [phoneState, setPhoneState] = useState({
    error: "",
    valid: "",
    isLoading: false,
  });
  const [usernameState, setUsernameState] = useState({
    error: "",
    valid: "",
    isLoading: false,
  });
  const [newAddressState, setNewAddressState] = useState({
    error: "",
    valid: "",
    isLoading: false,
  });
  const { checkout, setCheckout } = useCheckoutContext();
  const [errorBirthDateMessage, setErrorBirthDateMessage] = useState("");
  const [isBirthDateInvalid, setIsBirthDateInvalid] = useState(false);
  const [showTooltip, setShowTooltip] = useState(null);
  const streetAddressRef = useRef(null);
  const cityRef = useRef(null);
  const zipRef = useRef(null);

  const isAmbassadorOrAffiliate =
    checkout.enrollType === "ambassador" || checkout.enrollType === "affiliate";
  const isAmbassador = checkout.enrollType === "ambassador";
  const isUserLoggedIn = checkout.customerToken;

  const getCountryOptions = () => checkout.cart.hasPhysicalProducts ? PHYSICAL_COUNTRIES : VIRTUAL_COUNTRIES;

  const currentDate = new Date();
  const currentDateString = `${currentDate.getFullYear()}-${(
    "0" +
    (currentDate.getMonth() + 1)
  ).slice(-2)}-${("0" + currentDate.getDate()).slice(-2)}`;

  const validateAddressAndUpdateState = async () => {
    setNewAddressState((prevState) => ({
      ...prevState,
      isLoading: true,
      error: "",
      valid: "",
    }));

    const { streetAddress, city, zip, state } = checkout.newAddress;

    if (!streetAddress || !city || !zip || !state) {
      setNewAddressState((prevState) => ({
        ...prevState,
        error: "Street Address, City, Zip Code, and State are required",
        isLoading: false,
      }));
      return false;
    }

    try {
      const validAddress = await validateAddress(
        checkout.shopUrl,
        streetAddress,
        city,
        zip
      );

      if (validAddress && validAddress.success) {
        setCheckout((prevState) => ({
          ...prevState,
          newAddress: {
            ...prevState.newAddress,
            streetAddress: validAddress.address,
            city: validAddress.city,
            zip: validAddress.zip,
            state: validAddress.state,
          },
        }));

        setNewAddressState((prevState) => ({
          ...prevState,
          valid: "Address verified successfully",
          error: "",
          isLoading: false,
        }));

        setAddressConfirmed(false);
        setShowAddressConfirmation(false);
        setCheckout(prevState => ({
          ...prevState,
          addressValidatedManually: false
        }));

        return true;
      } else {
        throw new Error("Invalid address");
      }
    } catch (error) {
      setNewAddressState((prevState) => ({
        ...prevState,
        error: error.message || "Error validating address",
        valid: "",
        isLoading: false,
      }));

      setShowAddressConfirmation(true);

      return false;
    }
  };

  const verifyAddress = async () => {
    await validateAddressAndUpdateState();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    // Update referral validation to use the prop
    if ((referral === "Oola Life" || referral === "No referral") && !isUserLoggedIn) {
      if (!checkout.referralChoice) {
        alert("Please select how you discovered Oola Life");
        return;
      }
      // If they chose to find a referrer, make sure they actually selected one
      if (
        checkout.referralChoice === "find" &&
        (checkout.referredId === "Oola Life" || checkout.isSearching)
      ) {
        alert("Please select a referrer from the search results");
        return;
      }
    }

    // Validate birthdate for ambassador enrollment
    if (checkout.enrollType === "ambassador") {
      // Get birthdate from URL parameters
      const birthdateParam = getBirthdateFromUrl();

      if (birthdateParam) {
        // Store it in localStorage for inter-component communication
        saveBirthDateToLocalStorage(birthdateParam);
      }

      // Get customer_id from URL parameters if available
      const urlParams = new URLSearchParams(window.location.search);
      const shopCustomerId = urlParams.get("shopCustomerId");
      if (shopCustomerId && shopCustomerId.trim() !== "") {
        // Store in checkout context
        setCheckout((prev) => ({
          ...prev,
          shopCustomerId: shopCustomerId,
        }));

        // Also store in localStorage for backup
        localStorage.setItem("shopify_customer_id", shopCustomerId);
      }

      // Attempt to get birthdate from component state first
      let birthDate = checkout.newAddress?.birthDate;

      // If not in component state, try localStorage
      if (!birthDate || birthDate.trim() === "") {
        birthDate = getValidStoredBirthdate();

        // If found in localStorage, update component state
        if (birthDate) {
          setCheckout((prev) => ({
            ...prev,
            newAddress: {
              ...prev.newAddress,
              birthDate: birthDate,
            },
          }));
        } else {
          // Show a more user-friendly validation message for missing birthdate
          setErrorBirthDateMessage(
            "Birth date is required for Ambassador enrollment."
          );
          setIsBirthDateInvalid(true);

          // Scroll to the birth date field if it exists
          const birthDateField = document.getElementById("floatingBirthDate");
          if (birthDateField) {
            birthDateField.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
          return;
        }
      }

      // Validate that the person is at least 18 years old
      const selectedDate = new Date(birthDate);
      const currentDate = new Date();
      const adultDate = new Date(
        currentDate.getFullYear() - 18,
        currentDate.getMonth(),
        currentDate.getDate()
      );

      if (selectedDate > adultDate) {
        alert("You must be at least 18 years old to become an Ambassador.");
        return;
      }

      // Save birthdate to localStorage for use in later steps
      saveBirthDateToLocalStorage(birthDate);
    }

    if (
      checkout.shipping.newAddress !== null &&
      checkout.shipping.newAddress !== undefined &&
      !checkout.shipping.newAddress
    ) {
      await recalculateOrder(checkout);
      return;
    }

    setValidated(true);

    let isAddressValid = true;
    if (checkout.newAddress?.country === "US") {
      isAddressValid = await validateAddressAndUpdateState();

      if (!isAddressValid && addressConfirmed) {
        isAddressValid = true;
      }
    }

    if (!isAddressValid) {
      return;
    }

    if (
      form.checkValidity() === false ||
      emailState.error ||
      usernameState.error
    ) {
      event.stopPropagation();
      return;
    }

    if (checkout.newAddress.password !== checkout.newAddress.confirmPassword) {
      setPasswordMatch(false);
      return;
    }

    setPasswordMatch(true);
    setCheckout((prevCheckout) => ({
      ...prevCheckout,
      shipping: prevCheckout.newAddress,
    }));

    await recalculateOrder(checkout);
  };

  const validateEmail = async (email) => {
    if (!regexEmail.test(email)) {
      return { error: "Invalid email format", valid: "" };
    }

    try {
      const data = await checkEmailExists(email, checkout.shopUrl);

      if (data.emailExists === 0) {
        return { error: "", valid: "Email Valid" };
      } else {
        const message = emailExistsMessage(email);
        return { error: message, valid: "" };
      }
    } catch (error) {
      return { error: "Error validating email", valid: "" };
    }
  };

  const validatePhone = async (phone) => {
    if (!regexPhone.test(phone)) {
      return { error: "Invalid phone format", valid: "" };
    }

    return { error: "", valid: "Phone Valid" };
  };

  const validateContact = async (type, value, setState) => {
    setState((prevState) => ({ ...prevState, valid: "", error: "", isLoading: true }));

    try {
      let result;
      if (type === "email") {
        result = await validateEmail(value);
      } else if (type === "phone") {
        result = await validatePhone(value);
      }

      setState((prevState) => ({
        ...prevState,
        ...result,
        isLoading: false,
      }));
    } catch (error) {
      setState((prevState) => ({
        ...prevState,
        error: `Error validating ${type}`,
        isLoading: false,
      }));
    }
  };

  const handleRecalculateSuccess = (updatedCart) => {
    updateCheckoutState(updatedCart);
    setCalculating(false);
    const contentIds = updatedCart.orderItems?.map((item) => item.sku);
    const contents = updatedCart.orderItems?.map((item) => ({
      id: item.sku,
      quantity: item.quantity,
    }));

    // Facebook Pixel tracking
    ReactPixel.track("AddPaymentInfo", {
      content_ids: contentIds,
      contents: contents,
      currency: updatedCart?.currency,
      value: updatedCart?.orderTotal,
    });

    // TikTok Pixel tracking
    TiktokPixel.track("AddPaymentInfo", {
      contents: contents,
      currency: updatedCart?.currency,
      value: updatedCart?.orderTotal,
    });

    setCheckout((prevCheckout) => ({
      ...prevCheckout,
      currentStep: prevCheckout.currentStep + 1,
    }));
    nextStep();
  };

  const updateCheckoutState = (updatedCart) => {
    setCheckout((prevCheckout) => ({
      ...prevCheckout,
      cart: {
        ...prevCheckout.cart,
        shippingCost: updatedCart.shippingCost,
        shippingTotal: updatedCart.shippingTotal,
        shippingTotalString: updatedCart.shippingTotalString,
        orderTotal: updatedCart.orderTotal,
        orderTotalString: updatedCart.orderTotalString,
        subTotal: updatedCart.subTotal,
        totalDiscountRcString: updatedCart.totalDiscountRcString,
        totalDiscountRC: updatedCart.totalDiscountRC,
        taxTotal: updatedCart.taxTotal,
        taxTotalString: updatedCart.taxTotalString,
        discountOrder: updatedCart.discountOrder,
      },
    }));
  };

  const handleRecalculateError = (error) => {
    console.error("Error:", error);
  };

  const recalculateOrder = async (e) => {
    try {
      setCalculating(true);
      const updatedCart = await recalculateCart(checkout);

      const shouldSkipSubscription = () => {
        if (
          !updatedCart.autoOrderItems ||
          updatedCart.autoOrderItems.length === 0
        ) {
          return true;
        }

        if (updatedCart.autoOrderItems.length === 1) {
          const item = updatedCart.autoOrderItems[0];
          return item.productTitle === "Annual Ambassador Suite";
        }

        return false;
      };

      updateCheckoutState(updatedCart);
      setCalculating(false);

      const contentIds = updatedCart.orderItems?.map((item) => item.sku);
      const contents = updatedCart.orderItems?.map((item) => ({
        id: item.sku,
        quantity: item.quantity,
      }));

      ReactPixel.track("AddPaymentInfo", {
        content_ids: contentIds,
        contents: contents,
        currency: updatedCart?.currency,
        value: updatedCart?.orderTotal,
      });

      setCheckout((prevCheckout) => ({
        ...prevCheckout,
        currentStep: prevCheckout.currentStep + 1,
      }));

      if (shouldSkipSubscription()) {
        // Set default values for annual subscription
        const startDate = (() => {
          const date = new Date();
          date.setFullYear(date.getFullYear() + 1);
          return date.toISOString().split("T")[0];
        })();

        // Update context with annual configuration
        setCheckout((prev) => ({
          ...prev,
          subscription: {
            ...prev.subscription,
            frequency: "Yearly",
            startDate: startDate,
          },
        }));
      }

      nextStep();

    } catch (error) {
      handleRecalculateError(error);
    } finally {
      setCalculating(false);
    }
  };

  const pickShipping = (address) => {
    if (checkout.cart.hasPhysicalProducts && address.country !== "US") {
      setShowTooltip(address.address1);
      setTimeout(() => setShowTooltip(null), 3000);
      return;
    }

    // Preserve any birthdate information if we're in ambassador enrollment
    const existingBirthDate =
      checkout.enrollType === "ambassador"
        ? checkout.newAddress?.birthDate || ""
        : "";

    let newshipping = {
      firstName: address.firstName,
      lastName: address.lastName,
      phone: address.phone,
      country: address.country,
      streetAddress: address.address1,
      city: address.city,
      zip: address.zip,
      state: address.state,
      newAddress: false,
      // Include birthdate if available from existing profile or preserve it from the form
      birthDate: existingBirthDate,
    };
    setCheckout({ ...checkout, shipping: newshipping });

    // If we're selecting a saved address in ambassador mode, make sure we preserve the birthdate
    if (checkout.enrollType === "ambassador" && existingBirthDate) {
      localStorage.setItem("ambassador_birthdate", existingBirthDate);
    }
  };

  useEffect(() => {
    setCheckout({ ...checkout, currentStep: 1 });

    // Check for ambassador enrollment and look for birthdate in URL parameters
    if (checkout.enrollType === "ambassador") {
      // Try to get birthdate from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const birthdateParam = urlParams.get("birthdate");

      if (
        birthdateParam &&
        birthdateParam !== "null" &&
        birthdateParam !== "undefined"
      ) {
        // Validate the format (YYYY-MM-DD)
        if (/^\d{4}-\d{2}-\d{2}$/.test(birthdateParam)) {
          // Store it in localStorage for inter-component communication
          localStorage.setItem("ambassador_birthdate", birthdateParam);
        }
      }
    }


    if (checkout.newAddress == null) {
      setCheckout({
        ...checkout,
        newAddress: {
          email: "",
          firstName: "",
          lastName: "",
          username: "",
          birthDate: "",
          phone: "",
          password: "",
          loginEmail: "",
          confirmPassword: "",
          country: "US",
          streetAddress: "",
          city: "",
          zip: "",
          state: "",
          newAddress: true,
        },
      });
    }
  }, []);

  useEffect(() => {
    if (checkout.newAddress && !checkout.newAddress.country) {
      setCheckout({
        ...checkout,
        newAddress: {
          ...checkout.newAddress,
          country: "US",
        },
      });
    }
  }, [checkout.newAddress]);

  // Add a separate effect to log when enrollType changes
  useEffect(() => {
    if (checkout.enrollType === "ambassador") {
      const storedBirthDate = getValidStoredBirthdate();
      if (storedBirthDate) {
        // Update birthdate in checkout if not already set
        if (!checkout.newAddress?.birthDate) {
          setCheckout((prev) => ({
            ...prev,
            newAddress: {
              ...prev.newAddress,
              birthDate: storedBirthDate,
            },
          }));
        }
      }
    }
  }, [checkout.enrollType]);

  useEffect(() => {
    if (checkout.newAddress) {
      // Set initial state based on country
      const getInitialState = (country) => {
        switch (country) {
          case "CA":
            return "AB"; // First Canadian province (Alberta)
          case "AU":
            return "ACT"; // First Australian state (Australian Capital Territory)
          case "US":
          default:
            return "AL"; // First US state (Alabama)
        }
      };

      // If state is empty, set it to the first state of the selected country
      if (!checkout.newAddress.state) {
        setCheckout({
          ...checkout,
          newAddress: {
            ...checkout.newAddress,
            state: getInitialState(checkout.newAddress.country),
          },
        });
      }
    }
  }, [checkout.newAddress?.country]); // Run when country changes

  const clearValidationStates = () => {
    setNewAddressState((prevState) => ({
      ...prevState,
      error: "",
      valid: "",
      isLoading: false,
    }));
    if (streetAddressRef.current) {
      streetAddressRef.current.setCustomValidity("");
    }
    setAddressConfirmed(false);
    setShowAddressConfirmation(false);
    setCheckout(prevState => ({
      ...prevState,
      addressValidatedManually: false
    }));
  };

  return (
    <Container>
      <div className="wizardContainer">
        <button className="wizardLink">Cart</button>
        {">"}
        <button className="wizardLink">Account Details</button>
        {">"}
        <button className="wizardLink">Shipping Address</button>
        {">"}
        <button className="wizardLink" disabled>
          Payment
        </button>
      </div>

      <SummaryTable
        contact={contact}
        referral={referral}
        showReferralOptions={!isUserLoggedIn}
      />

      {filteredAddresses.length > 0 && (
        <Container className="p-0 my-3">
          {filteredAddresses.length > 0 && (
            <Row>
              <Col md={12} xs={12}>
                <h5>Select Your Shipping Address</h5>
              </Col>
              <Col xs={12}>
                <div className="horizontalRuler my-2" />
              </Col>
            </Row>
          )}
          <Row>
            {filteredAddresses.map((address, index) => (
              <div key={index}>
                <Row>
                  <Col md={12} xs={12}>
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <Form.Check
                        type="radio"
                        name="address"
                        id={`address-${index}`}
                        inline
                        onChange={() => pickShipping(address)}
                        checked={
                          checkout.shipping.streetAddress === address.address1
                        }
                      />
                      {address.country}, {address.city}, {address.address1}

                      {showTooltip === address.address1 && (
                        <div
                          className="tooltip-responsive"
                        >
                          <div
                            className="tooltip-responsive-arrow"
                          ></div>
                          We don't support shipping to your country, please select a new address. Thanks
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col xs={12}>
                    <div className="horizontalRuler my-2" />
                  </Col>
                </Row>
              </div>
            ))}
          </Row>
          {checkout.newAddress &&
            (checkout.newAddress.country ||
              checkout.newAddress.city ||
              checkout.newAddress.streetAddress) && (
              <Row>
                <Col md={12} xs={12}>
                  <Form.Check
                    type="radio"
                    name="address"
                    id={`address-new`}
                    inline
                    onChange={(e) => {
                      setCheckout((prevCheckout) => ({
                        ...prevCheckout,
                        shipping: prevCheckout.newAddress,
                      }));
                    }}
                    checked={
                      checkout.shipping.streetAddress ===
                      checkout.newAddress.streetAddress
                    }
                  />
                  {checkout.newAddress.country}, {checkout.newAddress.city},{" "}
                  {checkout.newAddress.streetAddress}
                </Col>
                <Col xs={12}>
                  <div className="horizontalRuler my-2" />
                </Col>
              </Row>
            )}
          <Row className="mt-5">
            <Col>
              <h5> Enter New Address</h5>
              <div className="horizontalRuler my-2" />
            </Col>
          </Row>
        </Container>
      )}

      <Container className="p-0">
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          {checkout.customerToken || !isAmbassadorOrAffiliate ? null : (
            <>
              <Row>
                <Col md={12} xs={12}>
                  <p>
                    {"Already have an account? "}
                    <a
                      href={SHOPIFY_LOGIN_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="oola-main-text-color"
                      style={{
                        width: "100%",
                        color: "white",
                        textDecoration: "none",
                      }}
                    >
                      Login
                    </a>
                  </p>
                </Col>
              </Row>
              <div className="box marginBottomSmall">
                <Row>
                  <Col md={9} xs={12}>
                    <h2 className="">
                      {isAmbassadorOrAffiliate
                        ? `Create Your ${checkout.enrollType === "ambassador"
                          ? "Ambassador"
                          : "Affiliate"
                        } Account`
                        : "Create Your Account"}
                    </h2>
                  </Col>
                </Row>
                {checkout.newAddress && (
                  <Row>
                    <Col xs="12" md="12">
                      {isAmbassadorOrAffiliate ? (
                        <>
                          <FloatingLabel
                            controlId="floatingUsername"
                            label="Username"
                            className="mb-3"
                          >
                            <Form.Control
                              type="text"
                              value={checkout.newAddress.username}
                              onChange={(e) => {
                                const username = e.target.value;
                                if (username.includes(" ")) {
                                  setUsernameState({
                                    valid: "",
                                    error: "Username should not contain spaces",
                                    isLoading: false,
                                  });
                                } else {
                                  setCheckout({
                                    ...checkout,
                                    newAddress: {
                                      ...checkout.newAddress,
                                      username: username,
                                    },
                                  });
                                }
                              }}
                              onBlur={async () => {
                                setUsernameState({
                                  valid: "",
                                  error: "",
                                  isLoading: true,
                                });
                                const result = await IsWebaliasAvailable(
                                  checkout.newAddress.username,
                                  checkout.shopUrl
                                );
                                setUsernameState(result);
                              }}
                              isInvalid={!!usernameState.error}
                              required
                            />
                            {usernameState.isLoading ? (
                              <Spinner
                                animation="border"
                                size="sm"
                                className="m-2"
                              />
                            ) : (
                              usernameState.valid && (
                                <p style={{ color: "green", margin: "4px" }}>
                                  {usernameState.valid}
                                </p>
                              )
                            )}
                            <Form.Control.Feedback type="invalid">
                              {usernameState.error}
                            </Form.Control.Feedback>
                            {checkout.enrollType === "ambassador" && (
                              <p className="user-login-url-form">
                                https://oolalife.com/?ref=
                                {checkout.newAddress.username}
                              </p>
                            )}
                          </FloatingLabel>
                          {isAmbassador && (
                            <FloatingLabel
                              controlId="floatingBirthDate"
                              label="Birth Date"
                              className="mb-3"
                            >
                              <Form.Control
                                type="date"
                                value={checkout.newAddress.birthDate}
                                onChange={(e) => {
                                  const selectedDate = new Date(e.target.value);
                                  const currentDate = new Date();
                                  const adultDate = new Date(
                                    currentDate.getFullYear() - 18,
                                    currentDate.getMonth(),
                                    currentDate.getDate()
                                  );

                                  setCheckout({
                                    ...checkout,
                                    newAddress: {
                                      ...checkout.newAddress,
                                      birthDate: e.target.value,
                                    },
                                  });

                                  if (selectedDate <= adultDate) {
                                    setErrorBirthDateMessage(""); // clear the error message
                                    setIsBirthDateInvalid(false); // set isBirthDateInvalid to false
                                    // Save valid birthdate to localStorage
                                    saveBirthDateToLocalStorage(e.target.value);
                                  } else {
                                    setErrorBirthDateMessage(
                                      "You must be at least 18 years old."
                                    );
                                    setIsBirthDateInvalid(true); // set isBirthDateInvalid to true
                                    // Don't save invalid birthdate
                                    localStorage.removeItem(
                                      "ambassador_birthdate"
                                    );
                                  }
                                }}
                                min="1924-01-02"
                                max={currentDateString}
                                required
                                isInvalid={isBirthDateInvalid} // add isInvalid attribute
                              />
                              <Form.Control.Feedback type="invalid">
                                {errorBirthDateMessage}
                              </Form.Control.Feedback>
                            </FloatingLabel>
                          )}
                        </>
                      ) : (
                        <FloatingLabel
                          controlId="floatingInput"
                          label="Email"
                          className="mb-3"
                        >
                          <Form.Control
                            type="email"
                            value={checkout.newAddress.loginEmail}
                            onChange={(e) => {
                              const email = e.target.value;
                              setCheckout({
                                ...checkout,
                                newAddress: {
                                  ...checkout.newAddress,
                                  loginEmail: email,
                                },
                              });
                            }}
                            onBlur={() =>
                              validateContact(
                                "email",
                                checkout.newAddress.loginEmail,
                                setEmailState
                              )
                            }
                            isInvalid={!!emailState.error}
                            required
                          />
                          {emailState.isLoading ? (
                            <Spinner
                              animation="border"
                              size="sm"
                              className="m-2"
                            />
                          ) : (
                            emailState.valid && (
                              <p style={{ color: "green", margin: "4px" }}>
                                {emailState.valid}
                              </p>
                            )
                          )}
                          <Form.Control.Feedback type="invalid" className="mt-0">
                            {emailState.error}
                          </Form.Control.Feedback>
                        </FloatingLabel>
                      )}
                    </Col>
                    <Col xs="12" md="6">
                      <Form.Group controlId="password">
                        <FloatingLabel
                          controlId="floatingPassword"
                          label="Password"
                          className="mb-3"
                        >
                          <Form.Control
                            type="password"
                            name="password"
                            autocomplete="new-password"
                            value={checkout.newAddress.password}
                            onChange={(e) => {
                              const password = e.target.value;
                              setCheckout({
                                ...checkout,
                                newAddress: {
                                  ...checkout.newAddress,
                                  password: password,
                                },
                              });
                              setPasswordMatch(
                                password === checkout.newAddress.confirmPassword
                              );
                            }}
                            required
                          />
                          <Form.Control.Feedback type="invalid" className="mt-0">
                            Please enter a password.
                          </Form.Control.Feedback>
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                    <Col xs="12" md="6">
                      <Form.Group controlId="confirmPassword">
                        <FloatingLabel
                          controlId="floatingConfirmPassword"
                          label="Confirm Password"
                          className="mb-3"
                        >
                          <Form.Control
                            type="password"
                            name="password_confirmation"
                            autocomplete="new-password"
                            value={checkout.newAddress.confirmPassword}
                            onChange={(e) => {
                              const confirmPassword = e.target.value;
                              setCheckout({
                                ...checkout,
                                newAddress: {
                                  ...checkout.newAddress,
                                  confirmPassword: confirmPassword,
                                },
                              });
                              setPasswordMatch(
                                checkout.newAddress.password === confirmPassword
                              );
                            }}
                            isInvalid={!passwordMatch}
                            // pattern={checkout.newAddress.password} // Use the pattern attribute to match the password
                            required
                          />
                          <Form.Control.Feedback type="invalid">
                            {passwordMatch
                              ? "Please confirm your password."
                              : "Passwords do not match."}
                          </Form.Control.Feedback>
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                  </Row>
                )}
              </div>
            </>
          )}

          {checkout.newAddress && (
            <div className="box marginBottomSmall">
              <h2 className="marginBottomSmall marginTopSmall">
                Shipping Address
              </h2>
              <Row>
                <Col xs="12" md="6" className="mb-4">
                  <FloatingLabel
                    controlId="floatingInput"
                    label="First Name"
                  >
                    <Form.Control
                      type="text"
                      value={checkout.newAddress.firstName}
                      onChange={(e) =>
                        setCheckout({
                          ...checkout,
                          newAddress: {
                            ...checkout.newAddress,
                            firstName: e.target.value,
                          },
                        })
                      }
                      maxLength="40"
                      required
                    />
                    <Form.Control.Feedback type="invalid" className="mt-0">
                      First Name is required
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Col>
                <Col xs="12" md="6" className="mb-4">
                  <FloatingLabel
                    controlId="floatingInput"
                    label="Last Name"
                  >
                    <Form.Control
                      type="text"
                      value={checkout.newAddress.lastName}
                      onChange={(e) =>
                        setCheckout({
                          ...checkout,
                          newAddress: {
                            ...checkout.newAddress,
                            lastName: e.target.value,
                          },
                        })
                      }
                      maxLength="40"
                      required
                    />
                    <Form.Control.Feedback type="invalid" className="mt-0">
                      Last Name is required
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Col>
              </Row>

              <FloatingLabel
                controlId="floatingEmail"
                label="Email"
                className="mb-4"
              >
                <Form.Control
                  type="email"
                  name="email"
                  autocomplete="email"
                  value={checkout.newAddress.email}
                  onChange={(e) =>
                    setCheckout({
                      ...checkout,
                      newAddress: {
                        ...checkout.newAddress,
                        email: e.target.value,
                      },
                    })
                  }
                  pattern="^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$"
                  required
                />
                <Form.Control.Feedback type="invalid" className="mt-0">
                  Please enter a valid email address
                </Form.Control.Feedback>
              </FloatingLabel>

              <FloatingLabel
                controlId="floatingPhone"
                label="Phone"
                className="mb-4"
              >
                <Form.Control
                  type="tel"
                  name="phone"
                  autocomplete="tel"
                  value={checkout.newAddress.phone}
                  onChange={(e) => {
                    const formattedPhoneNumber = formatPhoneNumber(
                      e.target.value
                    );
                    setCheckout({
                      ...checkout,
                      newAddress: {
                        ...checkout.newAddress,
                        phone: formattedPhoneNumber,
                      },
                    });
                  }}
                  onBlur={() =>
                    validateContact(
                      "phone",
                      checkout.newAddress.phone,
                      setPhoneState
                    )
                  }
                  isInvalid={
                    !!phoneState.error ||
                    (validated && !checkout.newAddress.phone)
                  }
                  required
                />
                {phoneState.isLoading ? (
                  <Spinner animation="border" size="sm" className="m-2" />
                ) : (
                  phoneState.valid && (
                    <p style={{ color: "green", margin: "4px" }}>
                      {phoneState.valid}
                    </p>
                  )
                )}
                <Form.Control.Feedback type="invalid" className="mt-0">
                  {phoneState.error || "Phone number is required"}
                </Form.Control.Feedback>
              </FloatingLabel>

              <FloatingLabel
                controlId="floatingSelect"
                label="Country"
                className="mb-4"
              >
                <Form.Select
                  aria-label="Country select"
                  value={checkout.newAddress.country || "US"}
                  onChange={(e) => {
                    const newCountry = e.target.value;
                    const getInitialState = (country) => {
                      switch (country) {
                        case "CA":
                          return "AB";
                        case "AU":
                          return "ACT";
                        case "US":
                        default:
                          return "AL";
                      }
                    };

                    setCheckout({
                      ...checkout,
                      newAddress: {
                        ...checkout.newAddress,
                        country: newCountry,
                        state: getInitialState(newCountry), // Reset state when country changes
                      },
                    });
                    clearValidationStates();
                  }}
                >
                  {getCountryOptions().map((country) => (
                    <option key={country[0]} value={country[0]}>
                      {country[1]}
                    </option>
                  ))}
                </Form.Select>
              </FloatingLabel>

              <FloatingLabel
                controlId="floatingInput"
                label="State"
                className="mb-4"
              >
                <Select
                  name="state"
                  value={checkout.newAddress.state}
                  className="payment-form-select"
                  collection={
                    checkout.newAddress.country === "CA"
                      ? "caProvince"
                      : checkout.newAddress.country === "AU"
                        ? "auState"
                        : "usState"
                  }
                  updateContext={(target, value) =>
                    setCheckout({
                      ...checkout,
                      newAddress: {
                        ...checkout.newAddress,
                        state: value,
                      },
                    })
                  }
                />
              </FloatingLabel>
              <FloatingLabel
                controlId="floatingStreetAddress"
                label="Street Address"
                className="mb-4"
              >
                <Form.Control
                  ref={streetAddressRef}
                  type="text"
                  name="address-line1"
                  autocomplete="street-address"
                  value={checkout.newAddress.streetAddress}
                  onChange={(e) => {
                    clearValidationStates();
                    setCheckout({
                      ...checkout,
                      newAddress: {
                        ...checkout.newAddress,
                        streetAddress: e.target.value,
                      },
                    });
                  }}
                  required
                  isInvalid={!!newAddressState.error}
                  isValid={!newAddressState.error && !!newAddressState.valid}
                />
                <Form.Control.Feedback type="invalid" className="mt-0">
                  {newAddressState.error || "Street Address is required"}
                </Form.Control.Feedback>
                {!newAddressState.error && newAddressState.valid && (
                  <Form.Control.Feedback type="valid">
                    {newAddressState.valid}
                  </Form.Control.Feedback>
                )}
              </FloatingLabel>

              <Row>
                <Col xs="12" md="6">
                  <FloatingLabel
                    controlId="floatingCity"
                    label="City"
                    className="mb-3"
                  >
                    <Form.Control
                      ref={cityRef}
                      type="text"
                      name="address-level2"
                      autocomplete="address-level2"
                      value={checkout.newAddress.city}
                      onChange={(e) => {
                        clearValidationStates();
                        setCheckout({
                          ...checkout,
                          newAddress: {
                            ...checkout.newAddress,
                            city: e.target.value,
                          },
                        });
                      }}
                      required
                      isInvalid={!!newAddressState.error}
                    />
                    <Form.Control.Feedback type="invalid" />
                  </FloatingLabel>
                </Col>
                <Col xs="12" md="6">
                  <FloatingLabel
                    controlId="floatingZip"
                    label="Zipcode"
                    className="mb-3"
                  >
                    <Form.Control
                      ref={zipRef}
                      type="text"
                      name="postal-code"
                      autocomplete="postal-code"
                      value={checkout.newAddress.zip}
                      onChange={(e) => {
                        clearValidationStates();
                        setCheckout({
                          ...checkout,
                          newAddress: {
                            ...checkout.newAddress,
                            zip: e.target.value,
                          },
                        });
                      }}
                      required
                      isInvalid={!!newAddressState.error}
                    />
                    <Form.Control.Feedback type="invalid" className="mt-0" />
                  </FloatingLabel>
                </Col>
              </Row>
              {showAddressConfirmation && (
                <div className="mb-2">
                  <div className="form-check mt-0">
                    <input
                      type="checkbox"
                      id="addressConfirmation"
                      checked={addressConfirmed}
                      onChange={(e) => {
                        setAddressConfirmed(e.target.checked);
                        setCheckout(prevState => ({
                          ...prevState,
                          addressValidatedManually: e.target.checked
                        }));
                      }}
                      className="form-check-input"
                      style={{
                        backgroundColor: addressConfirmed ? '#007bff' : '#fff',
                        borderColor: addressConfirmed ? '#007bff' : '#ced4da'
                      }}
                    />
                    <label htmlFor="addressConfirmation">
                      I confirm the Shipping Address is correct
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="flex JCEnd">
            {checkout.newAddress?.country === "US" && (
              <Button
                className="btn-bold secondary-color mx-3"
                variant="secondary"
                onClick={() => verifyAddress()}
                disabled={newAddressState.isLoading}
              >
                {newAddressState.isLoading && (
                  <Spinner animation="border" size="sm" className="me-2" />
                )}
                Verify Address
              </Button>
            )}
            <Button
              className="btn-bold oola-main-color"
              variant="primary"
              type="submit"
              disabled={isCalculating}
            >
              {isCalculating && (
                <Spinner animation="border" size="sm" className="me-2" />
              )}
              Ship to this address
            </Button>
          </div>
        </Form>
      </Container>
    </Container>
  );
};

export default ShippingAddress;
